import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaTag, FaArrowRight } from 'react-icons/fa';
import { theme } from '../../styles/theme';

// Page Header
const PageHeader = styled.div`
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('/images/blog-bg.jpg') no-repeat center center;
  background-size: cover;
  height: 40vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  margin-top: 80px;
`;

const PageTitle = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 3rem;
  text-align: center;
  max-width: 1200px;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

// Blog Section
const BlogSection = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
`;

const BlogWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const BlogHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const BlogSubheading = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 64px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

// Blog Grid
const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  grid-gap: 40px;
  
  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Blog Card
const BlogCard = styled(motion.div)`
  background: ${theme.colors.background.dark};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border: 1px solid ${theme.colors.secondary};
  
  &:hover {
    transform: translateY(-10px);
    transition: all 0.3s ease;
    box-shadow: 0 5px 15px rgba(212, 175, 55, 0.2);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  width: 100%;
  overflow: hidden;
`;

const BlogImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${BlogCard}:hover & {
    transform: scale(1.1);
  }
`;

const BlogContent = styled.div`
  padding: 24px;
`;

const BlogCategory = styled.span`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  display: inline-block;
  margin-bottom: 16px;
`;

const BlogTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: ${theme.colors.secondary};
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: ${theme.colors.tertiary};
    }
  }
`;

const BlogMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const BlogMetaItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: ${theme.colors.text.muted};
  margin-right: 20px;
  margin-bottom: 8px;
`;

const MetaIcon = styled.span`
  margin-right: 5px;
  color: ${theme.colors.secondary};
`;

const BlogExcerpt = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.secondary};
  font-weight: 600;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
    color: ${theme.colors.tertiary};
  }
`;

const ArrowIcon = styled(FaArrowRight)`
  margin-left: 8px;
  font-size: 0.8rem;
`;

// Featured Post
const FeaturedPost = styled.div`
  margin-bottom: 80px;
`;

const FeaturedCard = styled(motion.div)`
  display: flex;
  background: ${theme.colors.background.dark};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid ${theme.colors.secondary};
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const FeaturedImageContainer = styled.div`
  flex: 1;
  min-height: 400px;
  
  @media screen and (max-width: 768px) {
    min-height: 250px;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FeaturedContent = styled.div`
  flex: 1;
  padding: 40px;
  
  @media screen and (max-width: 768px) {
    padding: 24px;
  }
`;

const FeaturedLabel = styled.div`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  display: inline-block;
  margin-bottom: 16px;
  font-weight: 600;
`;

const FeaturedTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 16px;
  
  a {
    color: ${theme.colors.secondary};
    text-decoration: none;
    
    &:hover {
      color: ${theme.colors.tertiary};
    }
  }
  
  @media screen and (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const FeaturedMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const FeaturedExcerpt = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const FeaturedReadMore = styled(Link)`
  display: inline-block;
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.tertiary};
    transform: translateY(-3px);
  }
`;

// Pagination
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 64px;
`;

const PageNumber = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  margin: 0 5px;
  border-radius: 50%;
  background: ${({ active }) => (active ? theme.colors.secondary : theme.colors.primary)};
  color: ${({ active }) => (active ? theme.colors.text.secondary : theme.colors.text.primary)};
  text-decoration: none;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  
  &:hover {
    background: ${({ active }) => (active ? theme.colors.tertiary : theme.colors.secondary)};
    color: ${theme.colors.text.secondary};
  }
`;

const Blog = () => {
  // Sample blog posts data
  const blogPosts = [
    {
      id: 2,
      title: 'Introducing SA1stWorkers: Connecting South Africans with Trusted Local Professionals',
      category: 'Product Announcement',
      date: 'July 3, 2026',
      author: 'Afrotech Group',
      image: '/images/blog-2.jpg',
      excerpt: "Finding a reliable plumber, electrician, carpenter, cleaner, or handyman shouldn't be difficult. We're developing SA1stWorkers to connect customers with trusted local professionals while helping skilled workers grow their businesses.",
    },
    {
      id: 1,
      title: 'Launching DataFind: A New Era in Asset Protection',
      category: 'Product Launch',
      date: 'May 15, 2025',
      author: 'Bokang Mojela',
      image: '/datafindblogpic.png',
      excerpt: "We're excited to announce the official launch of DataFind, our innovative asset tracking and recovery platform designed to help law enforcement, businesses, and individuals protect their valuable assets.",
    },
    {
      id: 3,
      title: 'How Technology is Transforming Beauty Services in South Africa',
      category: 'Industry Insights',
      date: 'May 10, 2025',
      author: 'Team Member',
      image: '/images/blog-3.jpg',
      excerpt: "The beauty industry is undergoing a digital revolution, with mobile apps and on-demand services changing how customers access beauty treatments. Here's how our Beauty at Home platform is part of this transformation.",
    },
    {
      id: 4,
      title: 'The Future of Independent Journalism in Africa',
      category: 'Media',
      date: 'May 5, 2025',
      author: 'Team Member',
      image: '/images/blog-4.jpg',
      excerpt: 'With the rise of digital platforms, independent journalism is finding new ways to thrive. Our CloseUp News platform aims to support this ecosystem by providing tools for content creators to reach their audience.'
    },
    {
      id: 5,
      title: 'Partnering with Local Municipalities: Case Study',
      category: 'Partnerships',
      date: 'April 28, 2025',
      author: 'Bokang Mojela',
      image: '/images/blog-5.jpg',
      excerpt: 'Learn how our collaboration with local municipalities is helping to implement technology solutions that improve service delivery and community engagement.',
    },
    {
      id: 6,
      title: 'Building Tech for Africa: Challenges and Opportunities',
      category: 'Insights',
      date: 'April 20, 2025',
      author: 'Team Member',
      image: '/images/blog-6.jpg',
      excerpt: "Developing technology solutions for African markets comes with unique challenges and opportunities. Here's what we've learned on our journey so far.",
    },
    {
      id: 7,
      title: 'Afrotech Group Receives Innovation Award',
      category: 'Company News',
      date: 'April 15, 2025',
      author: 'Team Member',
      image: '/images/blog-7.jpg',
      excerpt: "We're honored to announce that Afrotech Group has been recognized with the Technology Innovation Award for our contributions to digital transformation in South Africa.",
    }
  ];

  // Featured post is the first post in the array
  const featuredPost = blogPosts[0];
  // Regular posts are all posts except the featured one
  const regularPosts = blogPosts.slice(1);

  return (
    <>
      <PageHeader>
        <PageTitle>Afrotech Insights</PageTitle>
      </PageHeader>
      
      <BlogSection>
        <BlogWrapper>
          <BlogHeading>Latest News & Updates</BlogHeading>
          <BlogSubheading>
            Stay up to date with the latest developments, insights, and stories from Afrotech Group and our community.
          </BlogSubheading>
          
          <FeaturedPost>
            <FeaturedCard
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FeaturedImageContainer>
                <FeaturedImage src={featuredPost.image} alt={featuredPost.title} />
              </FeaturedImageContainer>
              <FeaturedContent>
                <FeaturedLabel>Featured</FeaturedLabel>
                <FeaturedTitle>
                  <Link to={`/blog/${featuredPost.id}`}>{featuredPost.title}</Link>
                </FeaturedTitle>
                <FeaturedMeta>
                  <BlogMetaItem>
                    <MetaIcon><FaCalendarAlt /></MetaIcon>
                    {featuredPost.date}
                  </BlogMetaItem>
                  <BlogMetaItem>
                    <MetaIcon><FaUser /></MetaIcon>
                    {featuredPost.author}
                  </BlogMetaItem>
                  <BlogMetaItem>
                    <MetaIcon><FaTag /></MetaIcon>
                    {featuredPost.category}
                  </BlogMetaItem>
                </FeaturedMeta>
                <FeaturedExcerpt>
                  {featuredPost.excerpt}
                </FeaturedExcerpt>
                <FeaturedReadMore to={`/blog/${featuredPost.id}`}>
                  Read Full Article
                </FeaturedReadMore>
              </FeaturedContent>
            </FeaturedCard>
          </FeaturedPost>
          
          <BlogGrid>
            {regularPosts.map((post, index) => (
              <BlogCard
                key={post.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <BlogImage>
                  <BlogImg src={post.image} alt={post.title} />
                </BlogImage>
                <BlogContent>
                  <BlogCategory>{post.category}</BlogCategory>
                  <BlogTitle>
                    <Link to={`/blog/${post.id}`}>{post.title}</Link>
                  </BlogTitle>
                  <BlogMeta>
                    <BlogMetaItem>
                      <MetaIcon><FaCalendarAlt /></MetaIcon>
                      {post.date}
                    </BlogMetaItem>
                    <BlogMetaItem>
                      <MetaIcon><FaUser /></MetaIcon>
                      {post.author}
                    </BlogMetaItem>
                  </BlogMeta>
                  <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                  <ReadMoreLink to={`/blog/${post.id}`}>
                    Read More <ArrowIcon />
                  </ReadMoreLink>
                </BlogContent>
              </BlogCard>
            ))}
          </BlogGrid>
          
          <Pagination>
            <PageNumber active={true}>1</PageNumber>
            <PageNumber>2</PageNumber>
            <PageNumber>3</PageNumber>
            <PageNumber>4</PageNumber>
          </Pagination>
        </BlogWrapper>
      </BlogSection>
    </>
  );
};

export default Blog;
